import {ModelSetOptions} from 'backbone';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import {RNASequenceLinearMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface RNASequenceLinearMapTabStateAttributes
  extends SinglePolymerMapTabStateAttributes<Sequence, RNASequenceLinearMapState> {
  preferenceKey: string;
  visibilityOptions: vo.VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
}

interface RNASequenceLinearMapTabState {
  get<K extends keyof RNASequenceLinearMapTabStateAttributes>(
    key: K
  ): RNASequenceLinearMapTabStateAttributes[K];
  set<K extends keyof RNASequenceLinearMapTabStateAttributes>(
    attributeName: K,
    value: RNASequenceLinearMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): RNASequenceLinearMapTabState;
  set(obj: Partial<RNASequenceLinearMapTabStateAttributes>): RNASequenceLinearMapTabState;
}

class RNASequenceLinearMapTabState extends SinglePolymerMapTabState<Sequence, RNASequenceLinearMapState> {
  // Show one fewer base per slice to ensure phosphate mod on the last base is visible if present
  BASES_PER_SLICE_ADJUSTMENT = -1;

  mapState(options: {polymer: Sequence; visibilityOptions: VisibilityOptions}): RNASequenceLinearMapState {
    return new RNASequenceLinearMapState(options);
  }

  getVisibilityOptions(): VisibilityOptions {
    return this.get('visibilityOptions');
  }

  msVisibilityOptions(): vo.VisibilityOptions {
    return this.get('visibilityOptions').makeLocalSync();
  }

  defaults(): TabStateDefaults & Partial<RNASequenceLinearMapTabStateAttributes> {
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions('linear', [
        {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
        {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
        ...(BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS
          ? [
              {
                base: vo.PART_ANNOTATIONS,
                visible: true,
                editable: true,
                sync: true,
              },
            ]
          : []),
        {base: vo.PRIMERS, visible: true, editable: true, sync: true},
        {base: vo.PRIMER_BASES, visible: true, editable: true, sync: true},
        {base: vo.CUTSITES, visible: true, editable: true, sync: true},
        {base: vo.COMPLEMENT, visible: false, editable: true, sync: true},
        {base: vo.RNA_ORFS, visible: false, editable: true, sync: true},
        {base: vo.SEQUENCE_AXIS, visible: true, editable: true, sync: true},
        {base: vo.NUCLEOTIDE_STRUCTURE_IMAGES, visible: true, editable: true, sync: true},
        {base: vo.TRANSLATIONS, visible: true, editable: true, sync: true},
        {base: vo.AA_INDICES, visible: false, editable: false, sync: false},
        {base: vo.OVERHANGS, visible: false, editable: false, sync: false},
        {base: vo.DNA, visible: true, editable: false, sync: true},
        {base: vo.AMINO_ACIDS, visible: false, editable: false, sync: false},
        {base: vo.SEARCH_BASES, visible: true, editable: false, sync: true},
        {base: vo.PARTS, visible: true, editable: false},
      ]),
      preferenceKey: 'rna-sequence-linear-map',
    };
  }
}

export default RNASequenceLinearMapTabState;
